
import { useSelector } from "react-redux";
import { selectLoggedInUser } from "../../reducers/appSlice";
import {
  defaultGapping,
  defaultLayouts,
  defaultTypography,
  golfstatusTypography,
} from "golfstatus_react_components";
import {
  selectRegistrationCustomer,
  selectRegistrationOrder,
} from "../../reducers/registrationOrderSlice";
import PaymentMethodDetails from "../events/event/cart/payment-method-details";
import {
  isBankAttached,
  isCardAttached,
} from "../../forms/CartSections";
import CartFormLineItem from "../events/event/cart/cart-form-line-item";
import { faArrowRight, faExternalLinkSquare, faPen } from "@fortawesome/free-solid-svg-icons";
import CustomerLineItem from "../events/event/cart/customer-line-item";
import { usePaymentOptions, useTournament } from "../../hooks/tournamentHooks";
import { openExternalLink } from "../../helpers/Utilities";
import { useGolfstatusNavigate } from "../../hooks/navigationHooks";

const PaymentDetailsWidget = () => {
  const navigate = useGolfstatusNavigate();

  const currentUser = useSelector(selectLoggedInUser);
  const customer = useSelector(selectRegistrationCustomer);
  const registrationOrder = useSelector(selectRegistrationOrder);
  const tournament = useTournament()

  const {lastBank, currentPaymentOption, orderPaymentMethod} = usePaymentOptions();

  const cardIsAttached = isCardAttached(registrationOrder);
  const bankIsAttached = isBankAttached(registrationOrder);

  const label = currentUser?.id ? null : (
    <div>
      {"Add a card or  "}
      <span
        onClick={() => {
          window.location.href = process.env.REACT_APP_GOLFSTATUS_LOGIN_URL;
        }}
        style={{
          fontWeight: golfstatusTypography.boldFontWeight,
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        {"Sign In "}
      </span>
      {"to use a saved card."}
    </div>
  );

  const bankLabel = currentUser?.id ? null : (
    <div>
      <span
        onClick={() => {
          window.location.href = `${process.env.REACT_APP_GOLFSTATUS_LOGIN_URL}/create`;
        }}
        style={{
          fontWeight: golfstatusTypography.boldFontWeight,
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        {"Create an Account"}
      </span>
      {" or "}
      <span
        onClick={() => {
          window.location.href = process.env.REACT_APP_GOLFSTATUS_LOGIN_URL;
        }}
        style={{
          fontWeight: golfstatusTypography.boldFontWeight,
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        {"Sign In "}
      </span>
      {"to use a saved bank account."}
    </div>
  );

  const getCardDescription = () => {
    const { card } = registrationOrder;
    if (currentPaymentOption?.id) {
      return currentPaymentOption.subTitle;
    }
    if (registrationOrder?.internationalCardUsed) {
      return (
        <div
          style={{
            ...defaultLayouts.hStack.align("center", "center"),
            ...defaultGapping.smallGap,
            ...defaultLayouts.fullWidth,
            textWrap: "wrap",
          }}
        >
          {"This is an international card and is subject to additional fees."}
        </div>
      );
    }
    if (card) {
      return (
        <div
          style={{
            ...defaultLayouts.vStack.align("flex-start", "center"),
            ...defaultGapping.noGap,
            ...defaultLayouts.fullWidth,
            textWrap: "wrap",
          }}
        >
          <div>
            {card.brand} ending in {card.last4}
          </div>
        </div>
      );
    }
  };

  switch (orderPaymentMethod?.id) {
    case 1:
      return (
        <PaymentMethodDetails
          label="Credit/Debit Card"
          description={label}
          isPaymentAttached={cardIsAttached}
          content={
            <CartFormLineItem
              formName="Credit/Debit Card Details"
              required={true}
              status={cardIsAttached ? "complete" : "incomplete"}
              description={getCardDescription()}
              actions={[
                {
                  type: cardIsAttached ? "grey" : "orange",
                  title: cardIsAttached ? "Edit" : "Select Card",
                  style: { flexGrow: 1 },
                  buttonIcon: cardIsAttached ? faPen : "",
                  rightIcon: cardIsAttached ? "" : faArrowRight,
                  onClick: () => {
                    navigate("card");
                  },
                },
              ]}
            />
          }
        />
      );
    case 2:
      const hasBankAccount = !!lastBank;

      // default all fields to non-logged-in version
      let formName = "Paying with a Bank Account";
      let description = "Bank accounts need to be added to your GolfStatus profile before use.";
      let buttonTitle = "Add Bank Account";
      let status = undefined;
      let rightIcon = faExternalLinkSquare;
      let buttonIcon = null;

      if (currentUser?.id && bankIsAttached) {
        // if logged in and has a bank account selected
        status = "complete";
        formName = "Bank Account Details";
        description = currentPaymentOption?.subTitle;
        buttonTitle = "Edit";
        buttonIcon = faPen;
        rightIcon = null;
      } else if (currentUser?.id && hasBankAccount) {
        // if logged in, has bank account, but does not have a bank account selected
        status = "incomplete";
        formName = "Bank Account Details";
        description = null;
        rightIcon = faArrowRight;
        buttonTitle = "Select Bank Account";
      } else if (currentUser?.id) {
        // if logged in but has no bank accounts available
        formName = "Add Bank Account";
        description = "No bank account saved—add one to your GolfStatus profile to continue.";
      }

      return (
        <PaymentMethodDetails
          label="Bank Account"
          description={bankLabel}
          isPaymentAttached={bankIsAttached}
          content={
            <CartFormLineItem
              formName={formName}
              required={hasBankAccount || undefined}
              status={status}
              description={description}
              actions={[
                {
                  type: bankIsAttached ? "grey" : "orange",
                  title: buttonTitle,
                  rightIcon,
                  buttonIcon,
                  style: { flexGrow: 1 },
                  onClick: () => {
                    if (hasBankAccount) {
                      navigate("bank");
                    } else {
                      openExternalLink(`${process.env.REACT_APP_GOLFSTATUS_PROFILE_URL}/account-settings/add-payment-method?method=bank_account`);
                    }
                  },
                },
              ]}
            />
          }
        />
      );
    case 3:
      return (
        <PaymentMethodDetails
          label="Payment Disclaimer"
          description={
            `You are agreeing to register for ${tournament.name}. By tapping “Confirm Order” you acknowledge your obligation to pay the total to the tournament organizer. `
          }
        />
      );
    case 4:
      return (
        <PaymentMethodDetails
          isPaymentAttached={true}
          label="Invoice"
          description={
            "The invoice will be sent to the person below and will include the due date and payment instructions."
          }
          content={
            <CustomerLineItem
              {...customer}
              nameStyle={{ ...defaultTypography.mediumBoldBody }}
              editClick={() => {
                navigate("customer");
              }}
            />
          }
        />
      );
    default:
      return (
        <PaymentMethodDetails
          isPaymentAttached={true}
          label="Please Select A Payment Method"
          description={
            "Please select your preferred payment method from the options above."
          }
        />
      );
  }
};

export default PaymentDetailsWidget;
